import { apiService } from '../services/api'
import { INDEXED_FOLDERS_ENDPOINTS } from '../../api/endpoints'

interface ApiEnvelope<T> {
  code?: number
  message?: string
  data?: T
}

export interface IndexedFolderConfig {
  id: string
  name: string
  folderPath: string
  description?: string
  displayOrder: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface FolderIndexStatus {
  folderId: string
  documentCount: number
  folderCount: number
  lastIndexed?: string
  status: 'indexed' | 'pending' | 'indexing' | 'error'
  errorMessage?: string
}

export interface CreateFolderRequest {
  folderPath: string
  name: string
  description?: string | null
  displayOrder: number
  isActive: boolean
}

export interface UpdateFolderRequest {
  name?: string
  description?: string | null
  displayOrder?: number
  isActive?: boolean
}

export interface StartIndexingResponse {
  jobId: string
  status?: string
  crisisId?: string
  foldersToIndex?: string[]
  folderCount?: number
  message?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function extractEnvelopeData<T>(response: unknown): T | undefined {
  if (isRecord(response) && 'data' in response) {
    return (response as ApiEnvelope<T>).data
  }

  return response as T
}

function parseFolderStatus(raw: unknown, fallbackFolderId: string): FolderIndexStatus {
  if (!isRecord(raw)) {
    return {
      folderId: fallbackFolderId,
      documentCount: 0,
      folderCount: 0,
      status: 'pending',
    }
  }

  const status = raw.status
  const normalized: FolderIndexStatus = {
    folderId: typeof raw.folderId === 'string' ? raw.folderId : fallbackFolderId,
    documentCount: typeof raw.documentCount === 'number' ? raw.documentCount : 0,
    folderCount: typeof raw.folderCount === 'number' ? raw.folderCount : 0,
    status:
      status === 'indexed' || status === 'pending' || status === 'indexing' || status === 'error'
        ? status
        : 'pending',
  }

  if (typeof raw.lastIndexed === 'string') {
    normalized.lastIndexed = raw.lastIndexed
  }

  if (typeof raw.errorMessage === 'string') {
    normalized.errorMessage = raw.errorMessage
  }

  return normalized
}

function parseFolderConfig(raw: unknown): IndexedFolderConfig | null {
  if (!isRecord(raw)) {
    return null
  }

  if (
    typeof raw.id !== 'string' ||
    typeof raw.name !== 'string' ||
    typeof raw.folderPath !== 'string' ||
    typeof raw.displayOrder !== 'number' ||
    typeof raw.isActive !== 'boolean' ||
    typeof raw.createdAt !== 'string' ||
    typeof raw.updatedAt !== 'string'
  ) {
    return null
  }

  const folder: IndexedFolderConfig = {
    id: raw.id,
    name: raw.name,
    folderPath: raw.folderPath,
    displayOrder: raw.displayOrder,
    isActive: raw.isActive,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  }

  if (typeof raw.description === 'string') {
    folder.description = raw.description
  }

  return folder
}

function extractFolderArray(response: unknown): IndexedFolderConfig[] {
  const payload = extractEnvelopeData<unknown>(response)

  if (Array.isArray(payload)) {
    return payload
      .map(parseFolderConfig)
      .filter((folder): folder is IndexedFolderConfig => folder !== null)
  }

  if (isRecord(payload) && Array.isArray(payload.folders)) {
    return payload.folders
      .map(parseFolderConfig)
      .filter((folder): folder is IndexedFolderConfig => folder !== null)
  }

  return []
}

function extractFolderObject(response: unknown): IndexedFolderConfig {
  const payload = extractEnvelopeData<unknown>(response)
  const folder = parseFolderConfig(payload)

  if (folder) {
    return folder
  }

  throw new Error('Ongeldig antwoord van de server voor mapconfiguratie.')
}

export const indexedFoldersApi = {
  /**
   * Get all indexed folders
   */
  async fetchFolders(): Promise<IndexedFolderConfig[]> {
    const response = await apiService.get<unknown>(INDEXED_FOLDERS_ENDPOINTS.LIST)
    return extractFolderArray(response)
  },

  /**
   * Create a new indexed folder
   */
  async createFolder(config: CreateFolderRequest): Promise<IndexedFolderConfig> {
    const response = await apiService.post<unknown>(INDEXED_FOLDERS_ENDPOINTS.LIST, {
      folderPath: config.folderPath,
      name: config.name,
      description: config.description,
      displayOrder: config.displayOrder,
      isActive: config.isActive,
    })

    return extractFolderObject(response)
  },

  /**
   * Update an indexed folder
   */
  async updateFolder(id: string, config: UpdateFolderRequest): Promise<IndexedFolderConfig> {
    const payload: Record<string, unknown> = {}
    if (config.name !== undefined) payload.name = config.name
    if (config.description !== undefined) payload.description = config.description
    if (config.displayOrder !== undefined) payload.displayOrder = config.displayOrder
    if (config.isActive !== undefined) payload.isActive = config.isActive

    const response = await apiService.put<unknown>(
      INDEXED_FOLDERS_ENDPOINTS.FOLDER_BY_ID(id),
      payload
    )

    return extractFolderObject(response)
  },

  /**
   * Delete an indexed folder
   */
  async deleteFolder(id: string): Promise<void> {
    await apiService.delete(INDEXED_FOLDERS_ENDPOINTS.FOLDER_BY_ID(id))
  },

  /**
   * Toggle folder active status
   */
  async toggleFolder(id: string, isActive: boolean): Promise<IndexedFolderConfig> {
    const response = await apiService.patch<unknown>(INDEXED_FOLDERS_ENDPOINTS.TOGGLE(id), {
      isActive,
    })

    return extractFolderObject(response)
  },

  /**
   * Get status of a specific folder
   */
  async getFolderStatus(folderId: string): Promise<FolderIndexStatus> {
    const response = await apiService.get<unknown>(INDEXED_FOLDERS_ENDPOINTS.FOLDER_STATUS(folderId))
    const payload = extractEnvelopeData<unknown>(response)
    return parseFolderStatus(payload, folderId)
  },

  /**
   * Get status of all folders
   */
  async getAllFolderStatuses(): Promise<Record<string, FolderIndexStatus>> {
    const response = await apiService.get<unknown>(INDEXED_FOLDERS_ENDPOINTS.ALL_STATUSES)
    const payload = extractEnvelopeData<unknown>(response)

    if (!isRecord(payload)) {
      return {}
    }

    const normalized: Record<string, FolderIndexStatus> = {}
    for (const [folderId, rawStatus] of Object.entries(payload)) {
      normalized[folderId] = parseFolderStatus(rawStatus, folderId)
    }

    return normalized
  },

  /**
   * Start indexing for specified folders or all active folders if none specified
   */
  async startIndexing(folderIds?: string[]): Promise<StartIndexingResponse> {
    const payload = folderIds ? { folderIds } : {}
    const rawResponse = await apiService.post<unknown>(INDEXED_FOLDERS_ENDPOINTS.INDEX, payload)
    const data = extractEnvelopeData<unknown>(rawResponse)

    if (!isRecord(data) || typeof data.jobId !== 'string') {
      throw new Error('De indexeeropdracht kon niet worden gestart door een ongeldig serverantwoord.')
    }

    const normalizedResponse: StartIndexingResponse = {
      jobId: data.jobId,
    }

    if (typeof data.status === 'string') {
      normalizedResponse.status = data.status
    }
    if (typeof data.crisisId === 'string') {
      normalizedResponse.crisisId = data.crisisId
    }
    if (Array.isArray(data.foldersToIndex)) {
      normalizedResponse.foldersToIndex = data.foldersToIndex.filter(
        (folder): folder is string => typeof folder === 'string'
      )
    }
    if (typeof data.folderCount === 'number') {
      normalizedResponse.folderCount = data.folderCount
    }
    if (typeof data.message === 'string') {
      normalizedResponse.message = data.message
    }

    return normalizedResponse
  },
}
